'use client';

import { memo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Auth } from '@air/api';

import { type OAuthCustomState } from '~/components/auth/types';
import { Button, type ButtonVariants } from '~/components/Button';

export type AppleSignInButtonProps = {
  /** Plugin-side context to round-trip through Cognito to the post-auth
   * callback page. See `OAuthCustomState` for the supported fields. */
  customState?: OAuthCustomState;
  /** Callback to navigate to OAuth waiting page with the generated id */
  onNavigate: (id: string) => void;
  pluginName: string;
  /** Button size — defaults to 'large' for backwards compatibility */
  size?: ButtonVariants['size'];
};

export const AppleSignInButton = memo(
  ({ customState, onNavigate, pluginName, size = 'large' }: AppleSignInButtonProps) => {
    const handleClick = useCallback(() => {
      const id = uuidv4();

      // Spread customState first so caller fields can't shadow `id` or
      // `pluginName` — `onNavigate(id)` polls against the locally generated
      // UUID and a shadowed id would silently desync auth from polling.
      Auth.appleSignIn({ ...customState, id, pluginName });

      onNavigate(id);
    }, [customState, onNavigate, pluginName]);

    return (
      <Button appearance="outline" onClick={handleClick} prefix={<AppleIcon />} size={size}>
        Log in with Apple
      </Button>
    );
  },
);

AppleSignInButton.displayName = 'AppleSignInButton';

const AppleIcon = memo(() => (
  <svg aria-hidden="true" className="size-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
));

AppleIcon.displayName = 'AppleIcon';
